import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Lock, Unlock, Inbox, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = ['JazzCash', 'Easypaisa', 'Bank Transfer', 'Paid in Branch', 'Other'];

const STATUS_META = {
  pending:          { label: 'Pending',           tone: 'bg-yellow-500/20 text-yellow-500' },
  under_review:     { label: 'Under Review',      tone: 'bg-blue-500/20 text-blue-400' },
  pending_payment:  { label: 'Pending Payment',   tone: 'bg-amber-500/20 text-amber-400' },
  approved:         { label: 'Request Approved',  tone: 'bg-emerald-500/20 text-emerald-500' },
  rejected:         { label: 'Request Rejected',  tone: 'bg-red-500/20 text-red-500' },
};

const ADMIN_STATUS_OPTIONS = [
  { value: 'approved',        label: 'Request Approved' },
  { value: 'rejected',        label: 'Request Rejected' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'under_review',    label: 'Under Review' },
];

export default function LockUnlockManagement() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imeiOptions, setImeiOptions] = useState([]);
  const [paymentQrs, setPaymentQrs] = useState({});
  const [formData, setFormData] = useState({
    customer_id: '',
    account_number: '',
    imei: '',
    request_type: 'lock',
    reason: '',
    notes: '',
    payment_method: 'JazzCash',
    transaction_id: '',
  });
  const [statusDialog, setStatusDialog] = useState({ open: false, id: null, next: 'approved', reason: '' });

  const isApprover = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [reqRes, custRes, qrRes] = await Promise.all([
        axios.get(`${API_URL}/api/lock-unlock`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/settings/payment-qr`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setRequests(reqRes.data);
      setCustomers(custRes.data);
      setPaymentQrs(qrRes.data || {});
    } catch {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = async (customer_id) => {
    const c = customers.find((x) => x.id === customer_id);
    setFormData((f) => ({ ...f, customer_id, account_number: c?.account_number || '', imei: '' }));
    setImeiOptions([]);
    try {
      const { data } = await axios.get(`${API_URL}/api/lock-unlock/customer-imei/${customer_id}`,
        { withCredentials: true });
      const items = (data?.items || []).map((i) => ({
        value: i.imei || i.imei2 || i.serial_number,
        label: `${i.imei || i.imei2 || i.serial_number} · ${i.brand || ''} ${i.model || ''}`.trim(),
      })).filter((x) => x.value);
      setImeiOptions(items);
      if (items.length === 1) setFormData((f) => ({ ...f, imei: items[0].value }));
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      customer_id: formData.customer_id,
      account_number: formData.account_number,
      imei: formData.imei || null,
      request_type: formData.request_type,
      reason: formData.reason,
      notes: formData.notes || null,
    };
    if (formData.request_type === 'unlock') {
      body.payment_method = formData.payment_method;
      body.transaction_id = formData.transaction_id;
    }
    try {
      await axios.post(`${API_URL}/api/lock-unlock`, body, { withCredentials: true });
      toast.success('Request submitted');
      setDialogOpen(false);
      setFormData({ customer_id: '', account_number: '', imei: '', request_type: 'lock',
                    reason: '', notes: '', payment_method: 'JazzCash', transaction_id: '' });
      setImeiOptions([]);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const openStatusDialog = (id, next = 'approved') => setStatusDialog({ open: true, id, next, reason: '' });

  const submitStatus = async () => {
    const { id, next, reason } = statusDialog;
    if (next === 'rejected' && !reason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/lock-unlock/status/${id}`,
        { status: next, reason: reason || null }, { withCredentials: true });
      toast.success(`Status updated → ${STATUS_META[next]?.label || next}`);
      setStatusDialog({ open: false, id: null, next: 'approved', reason: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const getCustomerName = (id) => customers.find((c) => c.id === id)?.name || '—';

  const pending = requests.filter((r) => ['pending', 'under_review', 'pending_payment'].includes(r.status));
  const processed = requests.filter((r) => !['pending', 'under_review', 'pending_payment'].includes(r.status));

  const renderTable = (rows, isPendingList) => (
    rows.length === 0 ? (
      <div className="text-center py-8 text-slate-400">No requests</div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-400">Type</TableHead>
            <TableHead className="text-slate-400">Customer</TableHead>
            <TableHead className="text-slate-400">Account</TableHead>
            <TableHead className="text-slate-400">IMEI / Serial</TableHead>
            <TableHead className="text-slate-400">Payment</TableHead>
            <TableHead className="text-slate-400">Transaction ID</TableHead>
            <TableHead className="text-slate-400">Reason</TableHead>
            <TableHead className="text-slate-400">Status</TableHead>
            <TableHead className="text-slate-400">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="lock-request-row">
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                    r.request_type === 'lock' ? 'bg-red-500/20 text-red-500' : 'bg-emerald-500/20 text-emerald-500'
                  }`}>
                    {r.request_type === 'lock' ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    {r.request_type}
                  </span>
                  {r.auto_generated && (
                    <span
                      className="inline-flex items-center ml-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-orange-500/20 text-orange-400 border border-orange-500/40"
                      title={`Auto Lock Query · ${r.overdue_days || 0} days overdue on installment #${r.overdue_installment_no || '-'}`}
                      data-testid="auto-lock-query-badge"
                    >
                      AUTO · {r.overdue_days || 0}d
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-white">{getCustomerName(r.customer_id)}</TableCell>
                <TableCell className="text-slate-300">{r.account_number}</TableCell>
                <TableCell className="text-slate-300 font-mono text-xs">{r.imei || '—'}</TableCell>
                <TableCell className="text-slate-300 text-xs">{r.payment_method || '—'}</TableCell>
                <TableCell className="text-slate-300 font-mono text-xs">{r.transaction_id || '—'}</TableCell>
                <TableCell className="text-slate-300 max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-sm text-xs font-medium ${meta.tone}`}
                    data-testid={`status-${r.status}`}>{meta.label}</span>
                  {r.rejection_reason && (
                    <div className="text-xs text-red-400 mt-0.5" title={r.rejection_reason}>Reason: {r.rejection_reason}</div>
                  )}
                </TableCell>
                <TableCell>
                  {isPendingList && isApprover ? (
                    <Select onValueChange={(v) => openStatusDialog(r.id, v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 w-[190px] text-xs"
                        data-testid={`status-select-${r.id}`}>
                        <SelectValue placeholder="Set status…" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        {ADMIN_STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`status-option-${o.value}-${r.id}`}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-slate-500 text-xs">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    )
  );

  return (
    <div className="space-y-6" data-testid="lock-unlock-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="lockunlock-title">Lock / Unlock</h1>
          <p className="text-slate-400">Send lock requests for defaulters and unlock requests once payment is received</p>
        </div>
        <div className="flex items-center gap-2">
          {isApprover && (
            <Button
              variant="outline"
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              data-testid="refresh-lock-queries-button"
              onClick={async () => {
                try {
                  const { data } = await axios.post(`${API_URL}/api/lock-unlock/auto-populate`, {}, { withCredentials: true });
                  toast.success(`Lock Query refreshed · ${data.created} new, ${data.skipped} still open`);
                  fetchAll();
                } catch {
                  toast.error('Failed to refresh Lock Query');
                }
              }}
            >
              <Inbox className="h-4 w-4 mr-2" /> Refresh Lock Query
            </Button>
          )}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="new-request-button">
              <Plus className="h-4 w-4 mr-2" /> New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-white">New Lock / Unlock Request</DialogTitle>
              <DialogDescription className="text-slate-400">
                Pick the customer — the device IMEI is auto-populated from their inventory.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Customer *</Label>
                {formData.customer_id ? (
                  <div className="bg-slate-800 border border-amber-500/40 rounded-sm p-2 flex items-center justify-between"
                    data-testid="lock-customer-selected">
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">
                        {customers.find((c) => c.id === formData.customer_id)?.name || '—'}
                      </div>
                      <div className="text-slate-400 text-xs">{formData.account_number}</div>
                    </div>
                    <button type="button" className="text-slate-400 hover:text-red-400 text-xs"
                      onClick={() => {
                        setFormData((f) => ({ ...f, customer_id: '', account_number: '', imei: '' }));
                        setImeiOptions([]);
                        setCustomerSearch('');
                        setShowCustomerList(true);
                      }}>× Change</button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerList(true); }}
                      onFocus={() => setShowCustomerList(true)}
                      placeholder="Search by name, account, CNIC or mobile"
                      autoComplete="off"
                      data-testid="lock-customer-search"
                      className="bg-slate-800 border-slate-700 text-white" />
                    {showCustomerList && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto border border-slate-700 rounded-sm bg-slate-800 z-10"
                        data-testid="lock-customer-results">
                        {(() => {
                          const q = customerSearch.trim().toLowerCase();
                          const filtered = q
                            ? customers.filter((c) =>
                                (c.name || '').toLowerCase().includes(q)
                                || (c.account_number || '').toLowerCase().includes(q)
                                || (c.cnic || '').toLowerCase().includes(q)
                                || (c.mobile || '').toLowerCase().includes(q))
                            : customers.slice(0, 20);
                          return filtered.length ? filtered.slice(0, 30).map((c) => (
                            <button key={c.id} type="button"
                              onClick={() => { handleCustomerSelect(c.id); setShowCustomerList(false); setCustomerSearch(''); }}
                              data-testid={`lock-customer-pick-${c.id}`}
                              className="w-full text-left px-2 py-1.5 hover:bg-slate-700 text-xs border-b border-slate-700 last:border-b-0">
                              <div className="text-white">{c.name}
                                <span className="text-slate-500"> — {c.account_number}</span>
                              </div>
                              <div className="text-slate-400">{c.cnic} · {c.mobile}</div>
                            </button>
                          )) : (
                            <div className="p-2 text-slate-500 text-xs">No customers matched "{customerSearch}"</div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Request Type *</Label>
                  <Select value={formData.request_type}
                    onValueChange={(v) => setFormData({ ...formData, request_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="lock-request-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="lock">Lock</SelectItem>
                      <SelectItem value="unlock">Unlock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">IMEI / Serial (auto-populated)</Label>
                  {imeiOptions.length > 1 ? (
                    <Select value={formData.imei} onValueChange={(v) => setFormData({ ...formData, imei: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="lock-imei-select">
                        <SelectValue placeholder="Pick a device" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        {imeiOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={formData.imei} readOnly
                      placeholder={formData.customer_id ? 'No IMEI on file — enter manually' : 'Pick a customer first'}
                      onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
                      data-testid="lock-imei-input"
                      className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
                  )}
                </div>
              </div>

              {formData.request_type === 'unlock' && (
                <div className="grid grid-cols-2 gap-4 bg-slate-800/40 border border-emerald-500/30 rounded-sm p-3">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Payment Method *</Label>
                    <Select value={formData.payment_method}
                      onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="lock-payment-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Transaction ID *</Label>
                    <Input value={formData.transaction_id}
                      onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                      required data-testid="lock-transaction-id"
                      placeholder="e.g. 87HK21JN2X"
                      className="bg-slate-800 border-slate-700 text-white font-mono" />
                  </div>
                  {paymentQrs[formData.payment_method] ? (
                    <div className="col-span-2 flex flex-col sm:flex-row items-center gap-3 bg-slate-950/60 border border-slate-700 rounded-sm p-3">
                      <img src={paymentQrs[formData.payment_method]}
                        alt={`${formData.payment_method} QR`}
                        data-testid="payment-qr-preview"
                        className="h-40 w-40 bg-white p-2 rounded object-contain" />
                      <div className="text-xs text-slate-300 space-y-1">
                        <div className="text-emerald-400 font-semibold">Show this QR to the customer</div>
                        <div>1. Customer scans this code with {formData.payment_method}.</div>
                        <div>2. Customer completes the payment.</div>
                        <div>3. Copy the resulting Transaction ID into the field above.</div>
                        <a href={paymentQrs[formData.payment_method]}
                          download={`${formData.payment_method}-payment-qr.png`}
                          data-testid="payment-qr-download"
                          className="inline-block mt-1 text-amber-400 hover:text-amber-300 underline">
                          Download QR image
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="col-span-2 text-xs text-slate-400 bg-slate-950/40 border border-slate-700 rounded-sm p-2"
                      data-testid="payment-qr-empty">
                      No QR uploaded for {formData.payment_method}. Ask an admin to add one from Settings.
                    </div>
                  )}
                  <div className="col-span-2 text-xs text-slate-400 flex items-start gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-emerald-400" />
                    <span>Admin will verify this transaction ID before unlocking the device.</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">Reason *</Label>
                <Input value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required data-testid="lock-reason-input" className="bg-slate-800 border-slate-700 text-white"
                  placeholder={formData.request_type === 'lock' ? 'e.g. Missed 2 installments' : 'e.g. Paid Nov installment'} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" rows={2} />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-lock-request" className="bg-amber-500 hover:bg-amber-600">Submit Request</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="pending" data-testid="tab-pending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                <Inbox className="h-4 w-4 mr-2" /> In Queue ({pending.length})
              </TabsTrigger>
              <TabsTrigger value="processed" data-testid="tab-processed" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                Processed ({processed.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pending" className="mt-4">
              {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : renderTable(pending, true)}
            </TabsContent>
            <TabsContent value="processed" className="mt-4">
              {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : renderTable(processed, false)}
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>

      {/* Status change dialog — confirm / capture reason */}
      <Dialog open={statusDialog.open} onOpenChange={(o) => setStatusDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              Set to: {ADMIN_STATUS_OPTIONS.find((o) => o.value === statusDialog.next)?.label}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {statusDialog.next === 'rejected'
                ? 'Provide a reason so the officer knows why the request was rejected.'
                : statusDialog.next === 'pending_payment'
                ? 'Marks the request as awaiting the customer\'s payment.'
                : statusDialog.next === 'under_review'
                ? 'Ask the officer for more information before deciding.'
                : 'Approves the request. The device will be unlocked accordingly.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={statusDialog.reason}
              onChange={(e) => setStatusDialog((s) => ({ ...s, reason: e.target.value }))}
              placeholder={statusDialog.next === 'rejected' ? 'Reason for rejection (required)' : 'Optional note'}
              data-testid="status-reason-input"
              className="bg-slate-800 border-slate-700 text-white" rows={3} />
            <div className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setStatusDialog({ open: false, id: null, next: 'approved', reason: '' })}
                className="text-slate-300">Cancel</Button>
              <Button onClick={submitStatus} data-testid="status-confirm"
                className={statusDialog.next === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}>
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
